import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { 
  BookOpen, 
  Edit, 
  Save, 
  X, 
  Lightbulb,
  Users,
  Rocket,
  Heart,
  Building2,
  Calendar,
  Quote,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default content for the club story - can be edited by admins
const DEFAULT_STORY = {
  introduction: `# How the All American Dog Club Came to Be

The All American Dog Club wasn't born in a boardroom or a venture capital pitch meeting. It started the way all great ideas do—with a problem that needed solving and people passionate enough to solve it.`,

  the_problem: `## The Problem We Saw

Dog owners across America faced a fragmented landscape. Social media offered noise, not community. Existing platforms were built for engagement metrics, not meaningful connections. Breeders lacked proper lineage tracking. Local clubs operated in isolation. Pet services were scattered across dozens of apps and websites.

We saw dog lovers spending more time managing apps than enjoying time with their pets.`,

  the_vision: `## A Different Vision

What if there was one place where dog enthusiasts could:

- **Connect meaningfully** — not just scroll past photos, but actually collaborate
- **Find local resources** — parks, vets, groomers, trainers, all vetted by the community  
- **Track lineage** — proper ancestry and health records, not just cute photos
- **Participate in governance** — real clubs with real elections and real activities
- **Support rescues** — a directory of NGOs with transparent ratings and regional coverage

We envisioned more than an app. We envisioned a **platform for real-world collaboration** among dog enthusiasts.`,

  the_journey: `## The Journey

### Phase 1: Foundation (2024)
The initial concept emerged from conversations among dog show participants who felt isolated despite their shared passion. The first wireframes focused on three pillars: community, services, and pet records.

### Phase 2: Building (2025)
Development began in earnest. The core platform took shape: authentication, profiles, posts, forums, events. We added the features that make us unique: ELO-rated service providers, encrypted health records, the ancestry tracker.

### Phase 3: Beta Launch (2026)
We opened our doors to beta testers. Real users with real dogs helped us refine every feature. The adoption agency directory grew to 36 organizations across 5 US regions. The forum came alive with genuine discussions.`,

  the_partnership: `## How We're Structured

We picked our structure the way [AARP](https://en.wikipedia.org/wiki/AARP) picked theirs: **a member-owned nonprofit anchored to a separate operating company that builds and runs the technology.**

### The two sides

- **All American Dog Club** is organized as a **501(c)(4) social welfare organization.** Members own it, members vote on policy, and dues are the primary funding. The Club owns the brand, the member data, and the editorial firewall on partner recommendations.
- **World Pet Club Services, Inc. ("WPCS")** is a separate **Delaware C-corp** that builds the platform, ships the features, runs payments, and handles technical operations. WPCS is a *vendor* to the Club, not a parent.

The two are connected by a single document — a **Utility Services Agreement** — that lets WPCS invoice the Club at cost-plus fair-market-value rates. That FMV cap is what protects members from a runaway operator, and it's the same structural fence AARP, Consumer Reports, and Sierra Club use for their commercial arms.

### What each side does

**The Club (501(c)(4)):**
- Owns the member relationship and every member's data
- Runs member-elected governance and voting on policy
- Advocates against the private-equity roll-up of vet care, groomers, boarding, and pet food
- Approves the annual utility contract with WPCS through independent directors

**WPCS (Delaware C-corp):**
- Builds and hosts the platform, security, payments, and integrations
- Signs technology and partnership deals it owns directly (insurance affiliate deals, licensing, ad networks)
- Employs the engineering and product team at market rates
- Signs cost-plus contracts with other clubs beyond AADC (sub-clubs, kennel federations, WorldPets International)

### Why not just one entity?

Because 501(c)(4)s legitimately can't hire engineers at market rates without triggering IRS excess-benefit rules, can't take equity-style risk on new features with member dues, and can't be sold or acquired — which is exactly what makes the (c)(4) safe for members but a bad shape to build software inside. Putting the operator in a separate taxable C-corp is the standard fix.

### What this means for members

- **Your dues fund member services**, not shareholder returns. The utility contract caps what WPCS can charge.
- **Member data belongs to the Club**, not to WPCS. If WPCS ever ceased to be the Club's operator, member data would stay with the Club.
- **No PE-consolidated brands** on the Trusted Independents Directory. The editorial firewall is contractual, not aspirational.

You can read the full structure in the operations documentation, including the FMV utility-contract checklist and the entity map, if you want the receipts.`,

  looking_forward: `## Looking Forward

We're just getting started. On the roadmap:

- **Sub-communities** — Reddit-style groups for specific interests
- **NGO Rescue Program** — verified shelters can list adoptable pets
- **DNA Testing Partnerships** — health insights for responsible breeding
- **Corporate Partnerships** — deals and benefits for members

But the most important feature isn't on any roadmap. It's **you**—the members who make this community what it is.`,

  closing: `## Join the Story

Every member writes the next chapter. Whether you're a first-time dog owner or a championship breeder, whether you're looking for a local playdate or organizing a national event, there's a place for you here.

*Welcome to the All American Dog Club.*

---

*This story is a living document. As our community grows and evolves, so does our story. If you have memories, photos, or contributions from the early days, we'd love to include them.*`
};

const ClubStoryPage = () => {
  const { user, token } = useAuth();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.is_admin || user?.is_operator_admin || 
                  ['club_officer', 'ngo_officer'].includes(user?.member_class);

  useEffect(() => {
    fetchStory();
  }, []);

  const fetchStory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/club/story`);
      if (res.ok) {
        const data = await res.json();
        setStory(data);
      } else {
        // Use default if not saved yet
        setStory(DEFAULT_STORY);
      }
    } catch (error) {
      setStory(DEFAULT_STORY);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (section, value) => {
    setEditing(section);
    setEditValue(value);
  };

  const cancelEditing = () => {
    setEditing(null);
    setEditValue('');
  };

  const saveSection = async (section) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/club/story`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ [section]: editValue })
      });

      if (res.ok) {
        const updated = await res.json();
        setStory(prev => ({ ...prev, ...updated }));
        setEditing(null);
        toast.success('Section updated!');
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { key: 'introduction', title: 'Introduction', icon: BookOpen },
    { key: 'the_problem', title: 'The Problem', icon: Lightbulb },
    { key: 'the_vision', title: 'The Vision', icon: Star },
    { key: 'the_journey', title: 'The Journey', icon: Calendar },
    { key: 'the_partnership', title: 'How We\'re Structured', icon: Building2 },
    { key: 'looking_forward', title: 'Looking Forward', icon: Rocket },
    { key: 'closing', title: 'Join the Story', icon: Heart },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const content = story || DEFAULT_STORY;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white" data-testid="club-story-page">
      {/* Hero */}
      <div className="relative bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
            <BookOpen className="w-5 h-5" />
            <span className="text-sm font-medium">Our Story</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
            How It All Began
          </h1>
          <p className="text-xl text-amber-100 max-w-2xl mx-auto">
            The story behind the All American Dog Club — from idea to community
          </p>
        </div>
      </div>

      {/* Quote Banner */}
      <div className="bg-amber-100 border-y border-amber-200 py-8">
        <div className="container mx-auto px-4">
          <blockquote className="text-center max-w-3xl mx-auto">
            <Quote className="w-8 h-8 text-amber-400 mx-auto mb-4" />
            <p className="text-xl italic text-amber-800">
              "More than a community — a platform for real-world collaboration among dog enthusiasts."
            </p>
          </blockquote>
        </div>
      </div>

      {/* Admin Notice */}
      {isAdmin && (
        <div className="container mx-auto px-4 pt-8 max-w-4xl">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm">
              <strong>Admin Mode:</strong> You can edit any section of this story. 
              Use Markdown formatting for rich text.
            </p>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="space-y-8">
          {sections.map(({ key, title, icon: Icon }) => (
            <Card key={key} className="overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              {isAdmin && (
                <div className="bg-gray-50 border-b px-4 py-2 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    {title}
                  </span>
                  {editing !== key && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => startEditing(key, content[key] || DEFAULT_STORY[key] || '')}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  )}
                </div>
              )}
              <CardContent className="pt-6">
                {editing === key ? (
                  <div className="space-y-4">
                    <Textarea
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      rows={12}
                      className="font-mono text-sm"
                      placeholder="Enter content... (Markdown supported)"
                    />
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={cancelEditing}>
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                      <Button onClick={() => saveSection(key)} disabled={saving}>
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="prose prose-amber max-w-none prose-headings:font-heading prose-h2:text-2xl prose-h3:text-xl prose-li:marker:text-amber-500">
                    <ReactMarkdown>{content[key] || DEFAULT_STORY[key] || ''}</ReactMarkdown>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Timeline Visual */}
        <div className="mt-16">
          <h2 className="text-2xl font-heading font-bold text-center mb-8">Our Timeline</h2>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-amber-200" />
            {[
              { year: '2024', title: 'Concept & Design', desc: 'Initial wireframes and community research' },
              { year: '2025', title: 'Development', desc: 'Core platform built, features refined' },
              { year: '2026', title: 'Beta Launch', desc: 'Community testing, 36 NGOs onboarded' },
              { year: 'Future', title: 'Growth', desc: 'Sub-communities, partnerships, nationwide expansion' },
            ].map((item, i) => (
              <div key={item.year} className={`relative flex items-center mb-8 ${i % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                <div className={`w-5/12 ${i % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                  <div className="font-bold text-amber-600">{item.year}</div>
                  <div className="font-semibold">{item.title}</div>
                  <div className="text-sm text-muted-foreground">{item.desc}</div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-amber-500 rounded-full border-4 border-white shadow" />
                <div className="w-5/12" />
              </div>
            ))}
          </div>
        </div>

        {/* Footer CTA */}
        <div className="mt-16 text-center">
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="py-12">
              <Users className="w-12 h-12 text-amber-600 mx-auto mb-4" />
              <h3 className="text-2xl font-heading font-bold mb-2">Be Part of the Story</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Every member adds a new chapter. Join us and help write the future of dog community.
              </p>
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700">
                Join the Club
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClubStoryPage;
